import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

interface Question {
  id: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: 'A' | 'B' | 'C' | 'D';
  category: string;
  main_topic: string | null;
  difficulty: 'easy' | 'medium' | 'hard';
  date_active: string | null;
  last_asked_date: string | null;
  created_at: string;
  updated_at: string;
}

interface QuestionsResponse {
  success: boolean;
  data: {
    questions: Question[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface DifficultyStats {
  easy: number;
  medium: number;
  hard: number;
  total: number;
}

const AdminDifficultyManager: React.FC = () => {
  useDocumentTitle('Cognidaily | Difficulty Manager');
  const { isAuthenticated, refreshProfile } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [difficultyFilter, setDifficultyFilter] = useState<'all' | 'easy' | 'medium' | 'hard'>('all');
  const [search, setSearch] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [difficultyStats, setDifficultyStats] = useState<DifficultyStats>({ easy: 0, medium: 0, hard: 0, total: 0 });
  const [loadingStats, setLoadingStats] = useState(false);

  const limit = 200; // Request 200 per page

  // Refresh user profile on mount
  useEffect(() => {
    if (isAuthenticated) {
      refreshProfile().catch(err => {
        console.error('[AdminDifficultyManager] Failed to refresh profile:', err);
      });
    }
  }, [isAuthenticated, refreshProfile]);

  // Load difficulty statistics
  const loadDifficultyStats = async () => {
    try {
      setLoadingStats(true);
      const [easyRes, mediumRes, hardRes, totalRes] = await Promise.all([
        axios.get<QuestionsResponse>('/api/admin/questions', {
          params: { page: 1, limit: 1, difficulty: 'easy' }
        }),
        axios.get<QuestionsResponse>('/api/admin/questions', {
          params: { page: 1, limit: 1, difficulty: 'medium' }
        }),
        axios.get<QuestionsResponse>('/api/admin/questions', {
          params: { page: 1, limit: 1, difficulty: 'hard' }
        }),
        axios.get<QuestionsResponse>('/api/admin/questions', {
          params: { page: 1, limit: 1 }
        })
      ]);

      setDifficultyStats({
        easy: easyRes.data.data.pagination.total,
        medium: mediumRes.data.data.pagination.total,
        hard: hardRes.data.data.pagination.total,
        total: totalRes.data.data.pagination.total
      });
    } catch (err: any) {
      console.error('[AdminDifficultyManager] Failed to load difficulty stats', err);
    } finally {
      setLoadingStats(false);
    }
  };

  // Load questions
  const loadQuestions = async (pageNum: number, difficulty: string, searchText: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: pageNum,
        limit: limit
      };

      if (difficulty !== 'all') {
        params.difficulty = difficulty;
      }

      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const res = await axios.get<QuestionsResponse>('/api/admin/questions', { params });
      
      if (!res.data.success) {
        throw new Error('Failed to load questions');
      }

      setQuestions(res.data.data.questions || []);
      setPage(res.data.data.pagination.page);
      setTotalPages(res.data.data.pagination.totalPages);
      setTotal(res.data.data.pagination.total);
    } catch (err: any) {
      console.error('[AdminDifficultyManager] Failed to load questions', err);
      setError(err?.response?.data?.message || 'Failed to load questions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDifficultyStats();
  }, []);

  useEffect(() => {
    loadQuestions(1, difficultyFilter, search);
    setPage(1);
    setSelectedQuestions(new Set());
  }, [difficultyFilter, search]);

  useEffect(() => {
    loadQuestions(page, difficultyFilter, search);
  }, [page, difficultyFilter, search]);

  const toggleQuestionSelection = (questionId: string) => {
    setSelectedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (selectedQuestions.size === questions.length && questions.length > 0) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(questions.map(q => q.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.size === 0) return;
    const deleteCount = selectedQuestions.size;
    const confirmed = window.confirm(
      `Delete ${deleteCount} question${deleteCount === 1 ? '' : 's'}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await Promise.all(
        Array.from(selectedQuestions).map((id) =>
          axios.delete(`/api/admin/questions/${id}`, {
            headers: { 'X-Confirm-Action': 'DELETE_QUESTION' }
          })
        )
      );
      setSelectedQuestions(new Set());
      await loadQuestions(page, difficultyFilter, search);
      await loadDifficultyStats();
      showSuccess(`Deleted ${deleteCount} question(s).`);
    } catch (err: any) {
      console.error('[AdminDifficultyManager] Bulk delete failed', err);
      showError(err?.response?.data?.message || 'Failed to delete some questions. Please try again.');
    }
  };

  const getAnswerText = (question: Question, letter: 'A' | 'B' | 'C' | 'D'): string => {
    switch (letter) {
      case 'A': return question.option_a;
      case 'B': return question.option_b;
      case 'C': return question.option_c;
      case 'D': return question.option_d;
    }
  };

  const allSelected = questions.length > 0 && selectedQuestions.size === questions.length;

  return (
    <div className="admin-page">
      <SEOHead
        title="Admin | Difficulty Manager"
        description="Manage questions by difficulty level with overview statistics."
      />
      <header className="admin-page__header">
        <h1 className="admin-page__title">Difficulty</h1>
        <p className="admin-page__subtitle">
          Question difficulty distribution and management.
        </p>

        {/* Difficulty Statistics Overview */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <button
            type="button"
            onClick={() => setDifficultyFilter('easy')}
            style={{
              padding: '1rem',
              background: difficultyFilter === 'easy' 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'var(--surface-elevated, #ffffff)',
              borderRadius: '0.75rem',
              boxShadow: difficultyFilter === 'easy'
                ? '0 10px 30px rgba(16, 185, 129, 0.2)'
                : '0 10px 30px rgba(15, 23, 42, 0.08)',
              border: difficultyFilter === 'easy' 
                ? '2px solid #10b981' 
                : '2px solid #10b981',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              if (difficultyFilter !== 'easy') {
                e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 35px rgba(16, 185, 129, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (difficultyFilter !== 'easy') {
                e.currentTarget.style.background = 'var(--surface-elevated, #ffffff)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(15, 23, 42, 0.08)';
              }
            }}
          >
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginBottom: '0.5rem' }}>
              Easy
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#10b981' }}>
              {loadingStats ? '...' : difficultyStats.easy.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: '0.25rem' }}>
              {difficultyStats.total > 0 
                ? `${((difficultyStats.easy / difficultyStats.total) * 100).toFixed(1)}% of total`
                : '0%'}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setDifficultyFilter('medium')}
            style={{
              padding: '1rem',
              background: difficultyFilter === 'medium' 
                ? 'rgba(245, 158, 11, 0.1)' 
                : 'var(--surface-elevated, #ffffff)',
              borderRadius: '0.75rem',
              boxShadow: difficultyFilter === 'medium'
                ? '0 10px 30px rgba(245, 158, 11, 0.2)'
                : '0 10px 30px rgba(15, 23, 42, 0.08)',
              border: difficultyFilter === 'medium' 
                ? '2px solid #f59e0b' 
                : '2px solid #f59e0b',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              if (difficultyFilter !== 'medium') {
                e.currentTarget.style.background = 'rgba(245, 158, 11, 0.05)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 35px rgba(245, 158, 11, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (difficultyFilter !== 'medium') {
                e.currentTarget.style.background = 'var(--surface-elevated, #ffffff)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(15, 23, 42, 0.08)';
              }
            }}
          >
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginBottom: '0.5rem' }}>
              Medium
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#f59e0b' }}>
              {loadingStats ? '...' : difficultyStats.medium.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: '0.25rem' }}>
              {difficultyStats.total > 0 
                ? `${((difficultyStats.medium / difficultyStats.total) * 100).toFixed(1)}% of total`
                : '0%'}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setDifficultyFilter('hard')}
            style={{
              padding: '1rem',
              background: difficultyFilter === 'hard' 
                ? 'rgba(239, 68, 68, 0.1)' 
                : 'var(--surface-elevated, #ffffff)',
              borderRadius: '0.75rem',
              boxShadow: difficultyFilter === 'hard'
                ? '0 10px 30px rgba(239, 68, 68, 0.2)'
                : '0 10px 30px rgba(15, 23, 42, 0.08)',
              border: difficultyFilter === 'hard' 
                ? '2px solid #ef4444' 
                : '2px solid #ef4444',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              if (difficultyFilter !== 'hard') {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 35px rgba(239, 68, 68, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (difficultyFilter !== 'hard') {
                e.currentTarget.style.background = 'var(--surface-elevated, #ffffff)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(15, 23, 42, 0.08)';
              }
            }}
          >
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginBottom: '0.5rem' }}>
              Hard
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#ef4444' }}>
              {loadingStats ? '...' : difficultyStats.hard.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: '0.25rem' }}>
              {difficultyStats.total > 0 
                ? `${((difficultyStats.hard / difficultyStats.total) * 100).toFixed(1)}% of total`
                : '0%'}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setDifficultyFilter('all')}
            style={{
              padding: '1rem',
              background: difficultyFilter === 'all' 
                ? 'rgba(99, 102, 241, 0.1)' 
                : 'var(--surface-elevated, #ffffff)',
              borderRadius: '0.75rem',
              boxShadow: difficultyFilter === 'all'
                ? '0 10px 30px rgba(99, 102, 241, 0.2)'
                : '0 10px 30px rgba(15, 23, 42, 0.08)',
              border: difficultyFilter === 'all' 
                ? '2px solid #6366f1' 
                : '2px solid #6366f1',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              if (difficultyFilter !== 'all') {
                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 35px rgba(99, 102, 241, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (difficultyFilter !== 'all') {
                e.currentTarget.style.background = 'var(--surface-elevated, #ffffff)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(15, 23, 42, 0.08)';
              }
            }}
          >
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginBottom: '0.5rem' }}>
              Total
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#6366f1' }}>
              {loadingStats ? '...' : difficultyStats.total.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: '0.25rem' }}>
              Questions
            </div>
          </button>
        </div>
      </header>

        {/* Controls */}
        <div className="admin-page__filters">
          <input
            className="admin-page__search"
            placeholder="Search question text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button 
            type="button" 
            className="btn btn-secondary btn-xs" 
            onClick={() => {
              loadQuestions(page, difficultyFilter, search);
              loadDifficultyStats();
            }}
          >
            Refresh
          </button>
          {selectedQuestions.size > 0 && (
            <button
              type="button"
              className="btn btn-primary btn-xs"
              onClick={handleBulkDelete}
              style={{ background: '#dc2626', borderColor: '#dc2626' }}
            >
              Delete Selected ({selectedQuestions.size})
            </button>
          )}
        </div>

        {/* Questions Table */}
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : error ? (
          <div className="admin-error">
            <strong>Error:</strong> {error}
          </div>
        ) : questions.length === 0 ? (
          <p className="admin-loading">No questions found.</p>
        ) : (
          <div style={{ 
            background: 'var(--surface-elevated, #ffffff)',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden'
          }}>
            <div style={{ 
              padding: '1rem',
              borderBottom: '1px solid var(--color-border, #e5e7eb)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAllVisible}
                />
                Select all ({questions.length} visible)
              </label>
              {selectedQuestions.size > 0 && (
                <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: '0.85rem' }}>
                  {selectedQuestions.size} selected
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted, #f9fafb)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAllVisible}
                        style={{ marginRight: '0.5rem' }}
                      />
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      Difficulty
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      Topic / Subtopic
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      Question
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      Answers
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      Correct
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      Used in Quiz
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr key={q.id} style={{ borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedQuestions.has(q.id)}
                          onChange={() => toggleQuestionSelection(q.id)}
                        />
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span className={`admin-content-question__badge admin-content-question__badge--${q.difficulty}`}>
                          {q.difficulty}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                        {q.main_topic ? (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{q.main_topic}</div>
                            <div style={{ color: 'var(--text-muted, #6b7280)', fontSize: '0.8rem' }}>{q.category}</div>
                          </>
                        ) : (
                          <div style={{ color: 'var(--text-muted, #6b7280)' }}>{q.category}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', maxWidth: '400px' }}>
                        <div style={{ fontSize: '0.9rem' }}>{q.question_text}</div>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                        <div>A: {q.option_a}</div>
                        <div>B: {q.option_b}</div>
                        <div>C: {q.option_c}</div>
                        <div>D: {q.option_d}</div>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.9rem', fontWeight: 600 }}>
                        {q.correct_answer}: {getAnswerText(q, q.correct_answer)}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {q.date_active ? (
                          <span className="admin-content-question__badge admin-content-question__badge--used">
                            Yes ({new Date(q.date_active).toLocaleDateString()})
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: '0.85rem' }}>No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div style={{ 
              padding: '1rem',
              borderTop: '1px solid var(--color-border, #e5e7eb)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)' }}>
                Showing {questions.length} of {total.toLocaleString()} questions
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Prev
                </button>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AdminDifficultyManager;

